import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistoryGoBack, useStoreState } from "../../../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../../../util/mdmEntityDisplayNames";
import { useHistory, useParams } from "react-router-dom";
import { Notifications } from "../../../../../../components/Notifications/NotificationsContext";
import { useAuthContext } from "../../../../../../components/Auth";
import { useRoutePath } from "../../../../../../util/Routing";
import {
  BorderCard,
  PageTitle,
  PageWrapper,
} from "../../../../../../layout/portalPageLayout";
import { ContentWrapper } from "../../../../../../layout/portalPageLayout";
import useSWR from "swr";
import {
  AttributeTemplateSchema,
  TemplateProductFormSchema,
  TemplateProductFormStepItemSchema,
  TemplateProductFormStepSchema,
} from "../../../../../../types/types.PIM";
import axios, { AxiosError } from "axios";
import {
  PrimaryButtonMediumV2,
  DestructiveButtonMediumV2,
  ButtonWithConfirmDialog,
  DestructiveButtonLargeV2,
} from "../../../../../../components/Buttons/Buttons";
import { Breadcrumb } from "../../../../../../components/ProductsLayout/SharedProductsLayout";
import {
  H3,
  InnerTitleText,
  RegularTextSmall,
  SubheaderContentText,
} from "../../../../../../components/Typography/Typography";
import styled, { useTheme } from "styled-components";
import { TemplateFormStepCard } from "./TemplateFormStepCard";
import { TextField } from "../../../../../../components/TextFields/TextFields";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ExpandedState } from "@tanstack/react-table";
import {
  SubheaderContainer,
  SubheaderInformationContainer,
} from "../../../../../../components/Layout/Layout";
import {
  CalendarIcon,
  UserIcon,
} from "../../../../../../components/Icons/Icons";
import moment from "moment";
import { Loader } from "../../../../../../components/Loader/Loader";
import { ErrorPlaceholder } from "../../../../../../components/Error";
import { Form } from "../../../../../../layout/FormLayout";
import { zodRequiredString } from "../../../../../../util/zod.util";
import ReactTooltip from "react-tooltip";
import {
  borderPrimitiveTokens,
  spacingTokens,
  widthTokens,
} from "../../../../../../designTokens";
import { LinkUnstyled } from "../../../../../../components/LinkUnstyled/LinkUnstyled";

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.primaryBG};
  z-index: 10;
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 0;
    right: 0;
    background: linear-gradient(
      180deg,
      rgb(3 3 3 / 6%) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    height: 10px;
  }
`;
const SaveButtonContainer = styled.div`
  display: flex;
  align-content: center;
  align-items: center;
  margin-left: auto;
`;

export function TemplateFormCreator() {
  const [formData, setFormData] = useState<TemplateProductFormSchema>();
  const [showSaveButton, setshowSaveButton] = useState(false);
  const [expandedRows, setExpandedRows] = useState<{
    [key: string]: ExpandedState;
  }>({});
  const [steps, setSteps] = useState<TemplateProductFormStepSchema[]>([
    {
      name: "Step 1",
      id: crypto.randomUUID(),
      is_document_step: false,
      items: [],
    },
  ]);

  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { getTemplateDisplayName } = useMdmEntityDisplayNames();
  const { template_id, form_id } =
    useParams<{ template_id: string; form_id: string }>();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { hasPermission } = useAuthContext();
  const { historyGoBack } = useHistoryGoBack();
  const { adminPath } = useRoutePath();
  const { storefront_id } = useStoreState();
  const theme = useTheme();
  const history = useHistory();
  const { register, formState, errors, watch, setValue, handleSubmit } =
    useForm({
      resolver: zodResolver(
        z.object({
          form_name: zodRequiredString(t),
        })
      ),
    });
  const formName = watch("form_name");

  const { data: formDataResponse, error: formDataError } = useSWR<
    TemplateProductFormSchema,
    AxiosError
  >(
    form_id && form_id === "new"
      ? null
      : `/v2/storefronts/${storefront_id}/pim/templates/${template_id}/forms/${form_id}`,
    {
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (formDataResponse) {
      const updatedSteps: TemplateProductFormStepSchema[] =
        formDataResponse.steps.map((step) => {
          if (step.items && step.items.length) {
            return {
              ...step,
              items: step.items.map((item) => {
                if (item.columns && item.columns.length) {
                  const attributeMap = new Map(
                    item.attributes?.map((attr) => [attr.name, attr])
                  );

                  const ordered_attributes =
                    item.columns
                      .filter((col) => attributeMap.has(col.name))
                      .map((col) => attributeMap.get(col.name)) ?? [];
                  return {
                    ...item,
                    attributes: ordered_attributes,
                  } as TemplateProductFormStepItemSchema;
                }
                return item;
              }),
            };
          }
          return step;
        });
      const updatedFormDataResponse: TemplateProductFormSchema = {
        ...formDataResponse,
        steps: updatedSteps,
      };
      setFormData(updatedFormDataResponse);
      setSteps(updatedFormDataResponse?.steps ?? []);
      setValue("form_name", updatedFormDataResponse?.name ?? "");
    }
  }, [formDataResponse, setValue]);

  const {
    data: template,
    mutate: mutateTemplate,
    error: template_error,
  } = useSWR<AttributeTemplateSchema, AxiosError>(
    `/v2/tenants/${tenant_id}/pim/templates/${template_id}`,
    {
      revalidateOnFocus: false,
    }
  );

  const templateDisplayName = getTemplateDisplayName(
    template ?? {},
    t("Template")
  );

  const hasEmptySteps = useMemo(
    () =>
      steps
        .filter((step) => !step.is_document_step)
        .every((step) => step.items && step.items?.length === 0),
    [steps]
  );

  const handleStepExpandedState = (
    cardId: string,
    expandedState: ExpandedState
  ) => {
    setExpandedRows((prev) => {
      let newExpanded = prev;
      newExpanded[cardId] = expandedState;
      return newExpanded;
    });
  };
  const handleToggleAllRowsExpanded = (expanded: boolean) => {
    if (!expanded) {
      setExpandedRows({});
    }
  };

  const moveRow = ({
    item,
    source,
    data,
  }: {
    item: TemplateProductFormStepItemSchema;
    source: string;
    data: TemplateProductFormStepItemSchema[];
  }) => {
    // update the priority of the items
    const updated_data = data.map((item, item_index) => ({
      ...item,
      attributes: item.attributes.map((attr) => ({
        ...attr,
        priority: item_index + 1,
      })),
    }));
    const newCards = [...steps];
    const oldSource = newCards.find((card) => card?.items?.includes(item));
    const oldSourceIndex = oldSource ? newCards.indexOf(oldSource) : -1;
    const newSource = newCards.find((card) => card?.name === source);
    const newSourceIndex = newSource ? newCards.indexOf(newSource) : -1;
    if (oldSourceIndex === newSourceIndex) {
      newCards[oldSourceIndex].items = updated_data;
    } else {
      setshowSaveButton(true);
      if (oldSource && oldSourceIndex > -1) {
        const items = newCards[oldSourceIndex].items;
        items?.splice(oldSource.items?.indexOf(item) || 0, 1);
        newCards[oldSourceIndex].items = items;
      }
      if (newSource && newSourceIndex > -1) {
        newCards[newSourceIndex].items = updated_data;
      }
    }
    setSteps(newCards);
  };

  const updateStepItems = ({
    source,
    data,
  }: {
    source: string;
    data: TemplateProductFormStepItemSchema[];
  }) => {
    setshowSaveButton(true);
    const newCards = [...steps];
    const stepIndex = steps.findIndex((step) => step.name === source);
    newCards[stepIndex].items = data;
    setSteps(newCards);
  };

  const moveStep = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (dragIndex !== hoverIndex) {
        const list = [...steps];
        list.splice(hoverIndex, 0, list.splice(dragIndex, 1)[0]);
        setSteps(
          list.map((step, step_index) => ({
            ...step,
            priority: step_index + 1,
          }))
        );
        setshowSaveButton(true);
      }
    },
    [steps]
  );
  const handleAddItem = (
    step: TemplateProductFormStepSchema,
    items: TemplateProductFormStepItemSchema[]
  ) => {
    const newSteps = [...steps];
    const stepIndex = newSteps.findIndex((s) => s.id === step.id);
    if (stepIndex !== -1) {
      newSteps[stepIndex].items = items;
    }
    setSteps([]); // clear the steps to trigger the useEffect in the TemplateFormStepCard
    setSteps([...newSteps]);
  };

  const handleAddStep = () => {
    setSteps([
      ...steps,
      {
        id: crypto.randomUUID(),
        name: `Step ${steps.length + 1}`,
        is_document_step: false,
        items: [],
      },
    ]);
  };

  const handleAddDocuments = () => {
    setSteps([
      ...steps,
      {
        id: crypto.randomUUID(),
        name: `Documents`,
        is_document_step: true,
        categories: [],
      },
    ]);
  };

  const handleDeleteForm = async () => {
    try {
      await axios.delete(
        `/v2/storefronts/${storefront_id}/pim/templates/${template_id}/forms/${form_id}`
      );
      mutateTemplate();
      notifySuccess(t("Form has been deleted successfully."));
      historyGoBack(`${adminPath}/pim/templates/${template_id}/forms`);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error deleting the template form"),
        {
          error,
        }
      );
    }
  };

  const handleSave = async () => {
    try {
      form_id && form_id === "new"
        ? await axios.post(
            `/v2/storefronts/${storefront_id}/pim/templates/${template_id}/forms`,
            { steps, name: formName }
          )
        : await axios.put(
            `/v2/storefronts/${storefront_id}/pim/templates/${template_id}/forms/${form_id}`,
            {
              ...formData,
              steps,
              name: formName,
              description: "",
            }
          );
      await mutateTemplate();
      notifySuccess(t("Form saved successfully"));
      history.replace(`${adminPath}/pim/templates/${template_id}/forms`);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error saving the template form"),
        {
          error,
        }
      );
    }
  };

  const handleDeleteStep = (stepId: string) => {
    const newSteps = steps.filter((step) => step.id !== stepId);
    setSteps(newSteps);
    setshowSaveButton(true);
  };

  const handleEditStepName = (stepId: string, name: string) => {
    const newSteps = steps.map((step) =>
      step.id === stepId ? { ...step, name } : step
    );
    setSteps(newSteps);
    setshowSaveButton(true);
  };

  const updateDocumentCategories = (
    stepId: string,
    categories: { name: string; id: string }[]
  ) => {
    const newSteps = steps.map((step) =>
      step.id === stepId ? { ...step, categories: categories } : step
    );
    setSteps(newSteps);
    setshowSaveButton(true);
  };

  if (!template && !template_error && !formDataError) {
    return <Loader isLoading={true} />;
  }
  if (template_error || formDataError) {
    return (
      <ErrorPlaceholder
        message={t("There was an error loading the template form")}
      />
    );
  }

  return (
    <PageWrapper>
      <Breadcrumb
        style={{ padding: `${spacingTokens.spacing_xl}`, paddingBottom: 0 }}
      >
        <LinkUnstyled to={`${adminPath}/pim/templates`}>
          {t("Templates")}
        </LinkUnstyled>
        <div>/</div>
        <LinkUnstyled to={`${adminPath}/pim/templates/${template_id}/forms`}>
          {templateDisplayName}
        </LinkUnstyled>
        <div>/</div>
        <div>
          {formName ? formName : formDataResponse?.name ?? t("New Form")}
        </div>
      </Breadcrumb>
      <PageHeader>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <PageTitle style={{ margin: 0 }}>
            {formName ? formName : formDataResponse?.name ?? t("New Form")}
          </PageTitle>
          <SaveButtonContainer>
            <DestructiveButtonMediumV2
              onClick={() => {
                historyGoBack(
                  `${adminPath}/pim/templates/${template_id}/forms`
                );
              }}
              style={{ marginRight: "15px" }}
              type="reset"
              disabled={formState.isSubmitting}
            >
              {t("Cancel")}
            </DestructiveButtonMediumV2>
            <PrimaryButtonMediumV2
              onClick={handleSubmit(handleSave)}
              loading={formState.isSubmitting}
              datatip={
                hasEmptySteps
                  ? t(
                      "Cannot save the form with empty steps. Please add at least one field to the steps"
                    )
                  : undefined
              }
              datafor={hasEmptySteps ? "save-button-tooltip" : undefined}
              disabled={formState.isSubmitting || hasEmptySteps}
            >
              {t("Save")}
            </PrimaryButtonMediumV2>
          </SaveButtonContainer>
          {hasEmptySteps && (
            <ReactTooltip id="save-button-tooltip" effect="solid" />
          )}
        </div>
        {formData?.created_by && (
          <SubheaderInformationContainer>
            <SubheaderContainer>
              <UserIcon width={16} height={16} />
              <InnerTitleText>{t("Created By")}:</InnerTitleText>
              <SubheaderContentText>
                {formData.created_by && formData.created_by},{" "}
                {moment(formData.created_at).format("MMM,DD/YYYY [at] h:mm A")}
              </SubheaderContentText>
            </SubheaderContainer>
            <SubheaderContainer>
              <CalendarIcon width={16} height={16} />
              <InnerTitleText>{t("Last Modified")}:</InnerTitleText>
              <SubheaderContentText>
                {formData.modified_by && formData.modified_by},{" "}
                {moment(formData.modified_at).format("MMM,DD/YYYY [at] h:mm A")}
              </SubheaderContentText>
            </SubheaderContainer>
          </SubheaderInformationContainer>
        )}
      </PageHeader>
      <ContentWrapper
        style={{
          marginTop: theme.resolve("spacing_xl"),
          padding: theme.resolve("spacing_xl"),
        }}
      >
        <div
          style={{
            marginBottom: theme.resolve("spacing_3xl"),
            maxWidth: widthTokens.width_xs,
          }}
        >
          <Form onSubmit={handleSubmit(handleSave)}>
            <TextField
              name="form_name"
              label={t("Form Name")}
              autoComplete={"form_name"}
              defaultValue={formDataResponse?.name ?? ""}
              theref={register({
                required: true,
              })}
              formState={formState}
              errors={errors}
              type="text"
            />
          </Form>
        </div>
        <BorderCard>
          <div
            style={{
              paddingBottom: theme.resolve("spacing_sm"),
              marginBottom: theme.resolve("spacing_xl"),
              borderBottom: `${borderPrimitiveTokens.border_md} solid ${theme.secondaryBorder}`,
            }}
          >
            <H3 style={{ margin: 0 }}>{t("Form Builder")}</H3>
            <RegularTextSmall>
              {t("Configure form steps and add fields below")}
            </RegularTextSmall>
          </div>
          {template &&
            steps.map((step, i) => (
              <TemplateFormStepCard
                key={step.id}
                expandedRows={expandedRows[step.id]}
                handleExpandedState={handleStepExpandedState}
                template={template!}
                formId={formData?.id}
                index={i}
                card={step}
                formData={steps}
                moveStep={moveStep}
                moveRow={moveRow}
                updateStepItems={updateStepItems}
                handleAddItem={handleAddItem}
                handleToggleAllRowsExpanded={handleToggleAllRowsExpanded}
                handleAddStep={handleAddStep}
                handleAddDocuments={handleAddDocuments}
                updateDocumentCategories={updateDocumentCategories}
                handleDeleteStep={handleDeleteStep}
                handleEditStepName={handleEditStepName}
                handleDrop={
                  showSaveButton ? undefined : () => setshowSaveButton(true)
                }
                stepNames={steps.reduce((acc, st) => {
                  if (st.name !== step.name) {
                    acc.push(st.name.trim().toLocaleLowerCase());
                  }
                  return acc;
                }, [] as string[])}
              />
            ))}
        </BorderCard>
        <ButtonWithConfirmDialog
          Button={DestructiveButtonLargeV2}
          buttonText={t("Delete")}
          testid={"delete-template-form"}
          handleConfirm={() => handleDeleteForm()}
          confirmMessage={"Are you sure you want to delete this form?"}
          disabled={!hasPermission("delete_templates")}
        />
      </ContentWrapper>
    </PageWrapper>
  );
}
